import { useRouter } from "next/router";
import Link from "next/link";
import { IconType } from "react-icons";
import { FaPlay } from "react-icons/fa";
import { LuPalette } from "react-icons/lu";
import { IoMusicalNotes, IoText } from "react-icons/io5";
import { FaUsersRectangle } from "react-icons/fa6";
import { FaPhotoFilm } from "react-icons/fa6";
import { SlBubble } from "react-icons/sl";
import { type CompilationThemeId } from "@memento/types/compilation-theme";
import { notEmpty } from "@memento/library/type-witchcraft";
import { urlJoin } from "@memento/library/url";
import { cn } from "src/utils/cn";
import { OfflineLayout } from "src/layouts/OfflineLayout";
import config from "src/config";
import { ResilientImage } from "src/components/ResilientImage";
import { addUrlParams } from "src/libs/urlLib";

function Feature({
  heading,
  icon,
  iconClassName,
  children,
}: {
  heading: string;
  icon: IconType;
  iconClassName?: string;
  children: React.ReactNode;
}) {
  const Icon = icon;

  return (
    <div className="flex gap-4 text-gray-700 text-base sm:text-sm">
      <div>
        <Icon className={cn(iconClassName, "w-8 h-8")} />
      </div>
      <div className="flex flex-col gap-1">
        <h2 className="font-bold font-sans">{heading}</h2>
        <div className="leading-snug">{children}</div>
      </div>
    </div>
  );
}

function getThemeStillUrlFromThemeId(themeId: CompilationThemeId) {
  return urlJoin(
    config.cdn.STATIC_ASSETS_SUBDOMAIN,
    `/compilations/theme-stills/${themeId}.webp`
  );
}

export default function PreviewLandingPage() {
  const router = useRouter();
  const themeId = (router.query.theme as string) ?? undefined;
  const occasionId = (router.query.occasion as string) ?? undefined;
  const songId = (router.query.song as string) ?? undefined;
  const title = (router.query.title as string) ?? undefined;

  const demoPageUrl = addUrlParams("/group-videos/preview", {
    theme: themeId,
    occasion: occasionId,
    song: songId,
    title,
  });

  const sampleImageUrls = [
    themeId
      ? getThemeStillUrlFromThemeId(themeId as CompilationThemeId)
      : undefined,
    // putting this here so that we hopefully serve the image from the CDN
    // instead of from the Next server
    getThemeStillUrlFromThemeId("birthday-cake"),
    // fall back to a local image
    "/images/placeholders/compilation-theme-still-example.webp",
  ].filter(notEmpty);

  return (
    <OfflineLayout>
      <div className="flex flex-col gap-12 items-center my-12 max-w-2xl mx-auto">
        <h1 className="font-sans text-4xl font-light text-center ">
          Personalize Group Videos with Ease!
        </h1>
        <div className="relative">
          <ResilientImage
            className="aspect-video block object-cover border border-black rounded-md shadow-lg"
            urls={sampleImageUrls}
            width={1920}
            height={1080}
            alt=""
          />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <Link href={demoPageUrl}>
              <button
                type="button"
                className="bg-red-500 rounded-full drop-shadow-lg text-white py-4 px-6 flex gap-3 items-center text-xl whitespace-nowrap"
              >
                <FaPlay className="w-7 h-7" />
                <span>Try it Out</span>
              </button>
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Feature heading="Theme" icon={LuPalette}>
            Select a theme to set the mood
          </Feature>
          <Feature heading="Music" icon={IoMusicalNotes}>
            Choose music to complement the occasion
          </Feature>
          <Feature heading="Clips" icon={FaUsersRectangle}>
            Organize clips by dragging and dropping
          </Feature>
          <Feature heading="Add Photos" icon={FaPhotoFilm}>
            Upload the perfect video between clips
          </Feature>
          <Feature heading="Messages" icon={IoText}>
            Add personalized intro and outro messages
          </Feature>
          <Feature
            heading="Caption"
            icon={SlBubble}
            iconClassName="transform scale-x-[-1]"
          >
            Include captions (Name, Location, etc)
          </Feature>
        </div>
      </div>
    </OfflineLayout>
  );
}

PreviewLandingPage.offline = true;
PreviewLandingPage.enableSessionRecording = true;
